import React, { useState, useEffect } from 'react';

const NecrovaEmployeePortal = () => {
  // State for user information and portal features
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [todos, setTodos] = useState([]);
  const [newTodo, setNewTodo] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');

  // Custom notifications per user - would be pulled from a database in a real app
  const userNotifications = {
    admin: {
      mainNotification: "Welcome back, Administrator. Several security protocols require your attention.",
      secondaryNotification: "Quarterly security audit pending. Please review by Friday."
    },
    lunix: {
      mainNotification: "Welcome back, Founder. 3 new departmental reports are available for review.",
      secondaryNotification: "Board meeting materials prepared for your approval."
    },
    james: {
      mainNotification: "Welcome back, James. R&D lab results ready for your review.",
      secondaryNotification: "Project Asimov milestone achieved ahead of schedule."
    },
    jane: {
      mainNotification: "Welcome back, Jane. Your twins project is progressing well.",
      secondaryNotification: "New research materials uploaded to your secure drive."
    },
    guest: {
      mainNotification: "Welcome, Guest. Limited access granted to Necrova systems.",
      secondaryNotification: "Please contact your supervisor for additional clearance."
    }
  };

  // Simulate fetching user data from login or session
  useEffect(() => {
    // In a real app, this would come from authentication context or API
    const simulateUserLogin = () => {
      // Get username from URL parameter or localStorage in a real app
      // For demo, simulate logged in user - would be integrated with the login component
      const loggedInUser = new URLSearchParams(window.location.search).get('user') || 'admin';
      
      // Create blank employee details structure that can be filled in later
      const userProfile = {
        username: loggedInUser,
        fullName: '',
        department: '',
        position: '',
        employeeId: '',
        clearanceLevel: '',
        joinDate: '',
        supervisor: '',
        contactInfo: {
          email: '',
          phone: '',
          location: ''
        },
        projectAssignments: []
      };
      
      // Set up initial todos based on user
      const initialTodos = [
        { id: 1, text: "Review security protocols", completed: false },
        { id: 2, text: "Complete weekly report", completed: false },
        { id: 3, text: "Schedule department meeting", completed: false }
      ];

      setTimeout(() => {
        setUserData(userProfile);
        setTodos(initialTodos);
        setNotifications(userNotifications[loggedInUser] || userNotifications.guest);
        setLoading(false);
      }, 1500);
    };

    simulateUserLogin();
  }, []);

  // Handle adding new todo
  const handleAddTodo = (e) => {
    e.preventDefault();
    if (newTodo.trim()) {
      const newTodoItem = {
        id: Date.now(),
        text: newTodo,
        completed: false
      };
      setTodos([...todos, newTodoItem]);
      setNewTodo('');
    }
  };

  // Handle toggling todo completion
  const toggleTodoCompletion = (id) => {
    setTodos(todos.map(todo => 
      todo.id === id ? { ...todo, completed: !todo.completed } : todo
    ));
  };

  // Handle deleting todo
  const deleteTodo = (id) => {
    setTodos(todos.filter(todo => todo.id !== id));
  };

  // Loading screen
  if (loading) {
    return (
      <div className="necrova-portal-loading">
        <div className="loading-content">
        <img 
          src="https://cdn.4fingerstudios.com/Necrova/NecrovaStuffas/Full.png" 
          width="80" 
          height="80" 
          alt="Description of image"
        />
          <h1 className="loading-title">NECROVA</h1>
          <div className="loading-bar">
            <div className="loading-progress"></div>
          </div>
          <p className="loading-text">Initializing Employee Portal...</p>
        </div>
        <style jsx>{`
          .necrova-portal-loading {
            font-family: 'Arial', sans-serif;
            margin: 0;
            padding: 0;
            background: linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #16213e 100%);
            color: white;
            min-height: 100vh;
            display: flex;
            justify-content: center;
            align-items: center;
            position: relative;
            overflow: hidden;
          }
          
          .loading-content {
            text-align: center;
            animation: pulse 2s infinite;
          }
          
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.8; }
            100% { transform: scale(1); opacity: 1; }
          }
          
          .loading-title {
            font-size: 2rem;
            letter-spacing: 3px;
            margin: 1rem 0;
          }
          
          .loading-bar {
            width: 200px;
            height: 4px;
            background-color: rgba(138, 43, 226, 0.2);
            border-radius: 2px;
            margin: 1rem auto;
            overflow: hidden;
          }
          
          .loading-progress {
            height: 100%;
            background: linear-gradient(90deg, #8a2be2, #e94560);
            width: 0;
            animation: loadProgress 1.5s infinite;
          }
          
          @keyframes loadProgress {
            0% { width: 0; }
            50% { width: 100%; }
            100% { width: 0; }
          }
          
          .loading-text {
            color: #8a8aaa;
            font-size: 0.9rem;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="necrova-portal-container">
      <header className="portal-header">
        <div className="logo-section">
          <svg viewBox="0 0 50 50" width="40" height="40">
            <path d="M25,5 L45,25 L25,45 L5,25 Z" fill="#1a1a2e" stroke="#ffffff" strokeWidth="1" />
            <path d="M25,15 L35,25 L25,35 L15,25 Z" fill="#16213e" stroke="#8a2be2" strokeWidth="1" />
            <path d="M30,20 L40,25 L30,30" fill="#e94560" />
          </svg>
          <h1 className="portal-title">NECROVA</h1>
        </div>
        <div className="header-controls">
          <div className="time-display">{new Date().toLocaleTimeString()}</div>
          <a href="https://4fingerstudios.com/Necrova/Login" className="logout-button">
  <svg viewBox="0 0 24 24" width="16" height="16">
    <path
      d="M19 12H5M16 8l4 4-4 4"
      stroke="currentColor"
      fill="none"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
  Logout
</a>

        </div>
      </header>

      <div className="portal-content">
        <aside className="portal-sidebar">
          <div className="user-profile">
            <div className="avatar-placeholder">
              {userData.username.charAt(0).toUpperCase()}
            </div>
            <div className="user-info">
              <p className="user-name">{userData.username.toUpperCase()}</p>
              <p className="user-status">
                <span className="status-indicator"></span>
                Online
              </p>
            </div>
          </div>

          <nav className="portal-nav">
            <button 
              className={`nav-button ${activeTab === 'dashboard' ? 'active' : ''}`} 
              onClick={() => setActiveTab('dashboard')}
            >
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Dashboard
            </button>
            <button 
              className={`nav-button ${activeTab === 'profile' ? 'active' : ''}`} 
              onClick={() => setActiveTab('profile')}
            >
              <svg viewBox="0 0 24 24" width="20" height="20">
                <circle cx="12" cy="8" r="4" stroke="currentColor" fill="none" strokeWidth="2" />
                <path d="M5 20c0-3.5 3.5-7 7-7s7 3.5 7 7" stroke="currentColor" fill="none" strokeWidth="2" />
              </svg>
              Profile
            </button>
            <button 
              className={`nav-button ${activeTab === 'tasks' ? 'active' : ''}`} 
              onClick={() => setActiveTab('tasks')}
            >
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" fill="none" strokeWidth="2" />
              </svg>
              Tasks
            </button>
            <button 
              className={`nav-button ${activeTab === 'projects' ? 'active' : ''}`} 
              onClick={() => setActiveTab('projects')}
            >
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path d="M20 7l-8-4-8 4M5 10v6l7 3v-6M19 10v6l-7 3v-6" stroke="currentColor" fill="none" strokeWidth="2" />
              </svg>
              Projects
            </button>
            <button 
              className={`nav-button ${activeTab === 'files' ? 'active' : ''}`} 
              onClick={() => setActiveTab('files')}
            >
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path d="M5 4h14c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H5m14-14H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14" stroke="currentColor" fill="none" strokeWidth="2" />
                <path d="M8 8h8M8 12h4" stroke="currentColor" fill="none" strokeWidth="2" />
              </svg>
              Files
            </button>
          </nav>

          <div className="portal-footer">
            <p className="portal-version">SYSTEM V2.3.7</p>
            <p className="portal-copyright">&copy; {new Date().getFullYear()} NECROVA</p>
          </div>
        </aside>

        <main className="portal-main">
          {activeTab === 'dashboard' && (
            <div className="dashboard-content">
              <section className="welcome-banner">
                <div className="welcome-text">
                  <h2>{notifications.mainNotification || `Welcome back, ${userData.username}.`}</h2>
                  <p>{notifications.secondaryNotification || 'Your portal is ready.'}</p>
                </div>
                <div className="notification-badge">
                  <span>2</span>
                  <span>New</span>
                </div>
              </section>

              <div className="dashboard-grid">
                <section className="dashboard-card tasks-overview">
                  <div className="card-header">
                    <h3>Task List</h3>
                    <button className="view-all-button" onClick={() => setActiveTab('tasks')}>View All</button>
                  </div>
                  <div className="todos-container">
                    <ul className="todo-list">
                      {todos.slice(0, 4).map(todo => (
                        <li key={todo.id} className={`todo-item ${todo.completed ? 'completed' : ''}`}>
                          <label className="todo-checkbox">
                            <input 
                              type="checkbox" 
                              checked={todo.completed} 
                              onChange={() => toggleTodoCompletion(todo.id)} 
                            />
                            <span className="checkmark"></span>
                          </label>
                          <span className="todo-text">{todo.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="dashboard-card system-status">
                  <div className="card-header">
                    <h3>System Status</h3>
                  </div>
                  <div className="status-metrics">
                    <div className="metric">
                      <div className="metric-value">98.2%</div>
                      <div className="metric-label">System Uptime</div>
                      <div className="progress-bar">
                        <div className="progress" style={{ width: '98.2%' }}></div>
                      </div>
                    </div>
                    <div className="metric">
                      <div className="metric-value">23.4%</div>
                      <div className="metric-label">Resource Usage</div>
                      <div className="progress-bar">
                        <div className="progress" style={{ width: '23.4%' }}></div>
                      </div>
                    </div>
                    <div className="metric">
                      <div className="metric-value">86.7%</div>
                      <div className="metric-label">Project Completion</div>
                      <div className="progress-bar">
                        <div className="progress" style={{ width: '86.7%' }}></div>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="dashboard-card recent-activity">
                  <div className="card-header">
                    <h3>Recent Activity</h3>
                  </div>
                  <ul className="activity-list">
                    <li className="activity-item">
                      <div className="activity-icon document"></div>
                      <div className="activity-details">
                        <div className="activity-description">Project report uploaded</div>
                        <div className="activity-time">10 minutes ago</div>
                      </div>
                    </li>
                    <li className="activity-item">
                      <div className="activity-icon message"></div>
                      <div className="activity-details">
                        <div className="activity-description">New message from Director</div>
                        <div className="activity-time">1 hour ago</div>
                      </div>
                    </li>
                    <li className="activity-item">
                      <div className="activity-icon security"></div>
                      <div className="activity-details">
                        <div className="activity-description">Security protocol updated</div>
                        <div className="activity-time">3 hours ago</div>
                      </div>
                    </li>
                    <li className="activity-item">
                      <div className="activity-icon access"></div>
                      <div className="activity-details">
                        <div className="activity-description">Accessed Research Database</div>
                        <div className="activity-time">Yesterday</div>
                      </div>
                    </li>
                  </ul>
                </section>

                <section className="dashboard-card quick-access">
                  <div className="card-header">
                    <h3>Quick Access</h3>
                  </div>
                  <div className="quick-links">
                    <button className="quick-link-button">
                      <div className="button-icon document"></div>
                      <span>Documents</span>
                    </button>
                    <button className="quick-link-button">
                      <div className="button-icon calendar"></div>
                      <span>Calendar</span>
                    </button>
                    <button className="quick-link-button">
                      <div className="button-icon message"></div>
                      <span>Messages</span>
                    </button>
                    <button className="quick-link-button">
                      <div className="button-icon research"></div>
                      <span>Research</span>
                    </button>
                  </div>
                </section>
              </div>
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="profile-content">
              <section className="profile-header">
                <h2>Employee Profile</h2>
                <p className="profile-subtitle">View and manage your employee information</p>
              </section>

              <div className="profile-grid">
                <section className="profile-card personal-info">
                  <div className="card-header">
                    <h3>Personal Information</h3>
                    <button className="edit-button">
                      <svg viewBox="0 0 24 24" width="16" height="16">
                        <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5" stroke="currentColor" fill="none" strokeWidth="2" />
                        <path d="M17 3a2 2 0 012 2v.5l-9.5 9.5-4 1 1-4 9.5-9.5z" stroke="currentColor" fill="none" strokeWidth="2" />
                      </svg>
                      Edit
                    </button>
                  </div>
                  <div className="info-grid">
                    <div className="info-item">
                      <div className="info-label">Full Name</div>
                      <div className="info-value">{userData.fullName || '—'}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Employee ID</div>
                      <div className="info-value">{userData.employeeId || '—'}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Department</div>
                      <div className="info-value">{userData.department || '—'}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Position</div>
                      <div className="info-value">{userData.position || '—'}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Join Date</div>
                      <div className="info-value">{userData.joinDate || '—'}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Clearance Level</div>
                      <div className="info-value">{userData.clearanceLevel || '—'}</div>
                    </div>
                  </div>
                </section>

                <section className="profile-card contact-info">
                  <div className="card-header">
                    <h3>Contact Information</h3>
                    <button className="edit-button">
                      <svg viewBox="0 0 24 24" width="16" height="16">
                        <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5" stroke="currentColor" fill="none" strokeWidth="2" />
                        <path d="M17 3a2 2 0 012 2v.5l-9.5 9.5-4 1 1-4 9.5-9.5z" stroke="currentColor" fill="none" strokeWidth="2" />
                      </svg>
                      Edit
                    </button>
                  </div>
                  <div className="info-list">
                    <div className="info-item">
                      <div className="info-icon email"></div>
                      <div className="info-details">
                        <div className="info-label">Email</div>
                        <div className="info-value">{userData.contactInfo?.email || '—'}</div>
                      </div>
                    </div>
                    <div className="info-item">
                      <div className="info-icon phone"></div>
                      <div className="info-details">
                        <div className="info-label">Phone</div>
                        <div className="info-value">{userData.contactInfo?.phone || '—'}</div>
                      </div>
                    </div>
                    <div className="info-item">
                      <div className="info-icon location"></div>
                      <div className="info-details">
                        <div className="info-label">Office Location</div>
                        <div className="info-value">{userData.contactInfo?.location || '—'}</div>
                      </div>
                    </div>
                    <div className="info-item">
                      <div className="info-icon supervisor"></div>
                      <div className="info-details">
                        <div className="info-label">Supervisor</div>
                        <div className="info-value">{userData.supervisor || '—'}</div>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="profile-card assigned-projects">
                  <div className="card-header">
                    <h3>Assigned Projects</h3>
                  </div>
                  {userData.projectAssignments && userData.projectAssignments.length > 0 ? (
                    <ul className="projects-list">
                      {userData.projectAssignments.map((project, index) => (
                        <li key={index} className="project-item">
                          <div className="project-name">{project.name}</div>
                          <div className="project-role">{project.role}</div>
                          <div className="project-status">{project.status}</div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="empty-state">
                      <p>No projects assigned yet</p>
                    </div>
                  )}
                </section>
              </div>
            </div>
          )}

          {activeTab === 'tasks' && (
            <div className="tasks-content">
              <section className="tasks-header">
                <h2>Task Management</h2>
                <p className="tasks-subtitle">Track and manage your assigned tasks</p>
              </section>

              <div className="tasks-container">
                <div className="add-task-container">
                  <form onSubmit={handleAddTodo} className="add-task-form">
                    <input
                      type="text"
                      placeholder="Add new task..."
                      value={newTodo}
                      onChange={(e) => setNewTodo(e.target.value)}
                      className="task-input"
                    />
                    <button type="submit" className="add-task-button">
                      <svg viewBox="0 0 24 24" width="16" height="16">
                        <path d="M12 5v14M5 12h14" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                      Add Task
                    </button>
                  </form>
                </div>

                <div className="task-list-container">
                  <div className="task-filters">
                    <button className="filter-button active">All Tasks</button>
                    <button className="filter-button">In Progress</button>
                    <button className="filter-button">Completed</button>
                  </div>

                  <ul className="task-list">
                    {todos.length > 0 ? (
                      todos.map(todo => (
                        <li key={todo.id} className={`task-item ${todo.completed ? 'completed' : ''}`}>
                          <div className="task-main">
                            <label className="task-checkbox">
                              <input 
                                type="checkbox" 
                                checked={todo.completed} 
                                onChange={() => toggleTodoCompletion(todo.id)} 
                              />
                              <span className="checkmark"></span>
                            </label>
                            <span className="task-text">{todo.text}</span>
                          </div>
                          <div className="task-actions">
                            <button 
                              className="task-delete" 
                              onClick={() => deleteTodo(todo.id)}
                            >
                              <svg viewBox="0 0 24 24" width="16" height="16">
                                <path d="M6 6l12 12M6 18L18 6" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" />
                              </svg>
                            </button>
                          </div>
                        </li>
                      ))
                    ) : (
                      <div className="empty-state">
                        <p>No tasks available</p>
                        <p className="empty-subtext">Add a new task to get started</p>
                      </div>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'projects' && (
            <div className="placeholder-content">
              <div className="placeholder-icon projects"></div>
              <h2>Projects Section</h2>
              <p>Your assigned projects and collaborations will appear here</p>
            </div>
          )}

          {activeTab === 'files' && (
            <div className="placeholder-content">
              <div className="placeholder-icon files"></div>
              <h2>Files Section</h2>
              <p>Your documents and files will appear here</p>
            </div>
          )}
        </main>
      </div>
      <style jsx>{`
        .necrova-portal-container {
          font-family: 'Arial', sans-serif;
          margin: 0;
          padding: 0;
          background-color: #f0f2f5;
          color: #333;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
        }
        
        /* Header Styles */
        .portal-header {
          background: linear-gradient(90deg, #1a1a2e 0%, #16213e 100%);
          color: white;
          padding: 1rem 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
        }
        
        .logo-section {
          display: flex;
          align-items: center;
        }
        
        .portal-title {
          margin-left: 1rem;
          font-size: 1.5rem;
          letter-spacing: 2px;
        }
        
        .header-controls {
          display: flex;
          align-items: center;
        }
        
        .time-display {
          margin-right: 1.5rem;
          font-size: 0.9rem;
          color: #aaa;
        }
        
        .logout-button {
          background-color: transparent;
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 4px;
          padding: 0.5rem 1rem;
          display: flex;
          align-items: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .logout-button svg {
          margin-right: 0.5rem;
        }
        
        .logout-button:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
        
        /* Portal Content Styles */
        .portal-content {
          display: flex;
          flex: 1;
          position: relative;
        }
        
        /* Sidebar Styles */
        .portal-sidebar {
          width: 240px;
          background-color: #1a1a2e;
          color: white;
          padding: 1.5rem 0;
          display: flex;
          flex-direction: column;
          height: calc(100vh - 74px);
          position: sticky;
          top: 0;
        }
        
        .user-profile {
          display: flex;
          align-items: center;
          padding: 0 1.5rem 1.5rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .avatar-placeholder {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: #8a2be2;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          margin-right: 0.75rem;
        }
        
        .user-info {
          flex: 1;
        }
        
        .user-name {
          font-size: 0.85rem;
          font-weight: 600;
          margin: 0 0 0.2rem;
        }
        
        .user-status {
          font-size: 0.75rem;
          color: #aaa;
          margin: 0;
          display: flex;
          align-items: center;
        }
        
        .status-indicator {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #4CAF50;
          margin-right: 0.5rem;
        }
        
        .portal-nav {
          flex: 1;
          display: flex;
          flex-direction: column;
          padding: 1.5rem 0;
        }
        
        .nav-button {
          background-color: transparent;
          color: #aaa;
          border: none;
          text-align: left;
          padding: 0.75rem 1.5rem;
          display: flex;
          align-items: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .nav-button svg {
          margin-right: 0.75rem;
        }
        
        .nav-button:hover {
          background-color: rgba(255, 255, 255, 0.05);
          color: white;
        }
        
        .nav-button.active {
          background-color: rgba(138, 43, 226, 0.2);
          color: white;
          border-left: 3px solid #8a2be2;
        }
        
        .portal-footer {
          padding: 1.5rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          text-align: center;
        }
        
        .portal-version {
          font-size: 0.7rem;
          color: #555;
          margin: 0 0 0.5rem;
        }
        
        .portal-copyright {
          font-size: 0.7rem;
          color: #777;
          margin: 0;
        }
        
        /* Main Content Styles */
        .portal-main {
          flex: 1;
          padding: 2rem;
          overflow-y: auto;
        }
        
        /* Dashboard Styles */
        .welcome-banner {
          background: linear-gradient(135deg, #16213e 0%, #1a1a2e 100%);
          color: white;
          padding: 2rem;
          border-radius: 8px;
          margin-bottom: 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        
        .welcome-text h2 {
          margin: 0 0 0.5rem;
          font-size: 1.5rem;
        }
        
        .welcome-text p {
          margin: 0;
          opacity: 0.8;
        }
        
        .notification-badge {
          background-color: #e94560;
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 4px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        
        .notification-badge span:first-child {
          font-size: 1.5rem;
          font-weight: bold;
        }
        
        .notification-badge span:last-child {
          font-size: 0.75rem;
          text-transform: uppercase;
        }
        
        .dashboard-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
        }
        
        .dashboard-card {
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }
        
        .card-header {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #eee;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .card-header h3 {
          margin: 0;
          font-size: 1.1rem;
          color: #333;
        }
        
        .view-all-button {
          background-color: transparent;
          color: #8a2be2;
          border: none;
          font-size: 0.85rem;
          cursor: pointer;
        }
        
        .view-all-button:hover {
          text-decoration: underline;
        }
        
        /* Tasks Overview Styles */
        .todos-container {
          padding: 1rem 1.5rem;
        }
        
        .todo-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .todo-item {
          display: flex;
          align-items: center;
          padding: 0.75rem 0;
          border-bottom: 1px solid #f0f0f0;
        }
        
        .todo-item:last-child {
          border-bottom: none;
        }
        
        .todo-checkbox {
          position: relative;
          display: inline-block;
          width: 20px;
          height: 20px;
          margin-right: 0.75rem;
        }
        
        .todo-checkbox input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        
        .checkmark {
          position: absolute;
          top: 0;
          left: 0;
          width: 18px;
          height: 18px;
          border: 1px solid #ccc;
          border-radius: 3px;
          background-color: white;
        }
        
        .todo-checkbox input:checked ~ .checkmark {
          background-color: #8a2be2;
          border-color: #8a2be2;
        }
        
        .checkmark:after {
          content: "";
          position: absolute;
          display: none;
        }
        
        .todo-checkbox input:checked ~ .checkmark:after {
          display: block;
          left: 6px;
          top: 2px;
          width: 5px;
          height: 10px;
          border: solid white;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }
        
        .todo-text {
          font-size: 0.9rem;
          color: #333;
        }
        
        .todo-item.completed .todo-text {
          text-decoration: line-through;
          color: #aaa;
        }
        
        /* System Status Styles */
        .status-metrics {
          padding: 1rem 1.5rem;
        }
        
        .metric {
          margin-bottom: 1rem;
        }
        
        .metric:last-child {
          margin-bottom: 0;
        }
        
        .metric-value {
          font-size: 1.5rem;
          font-weight: bold;
          color: #333;
        }
        
        .metric-label {
          font-size: 0.85rem;
          color: #777;
          margin-bottom: 0.5rem;
        }
        
        .progress-bar {
          width: 100%;
          height: 6px;
          background-color: #f0f0f0;
          border-radius: 3px;
          overflow: hidden;
        }
        
        .progress {
          height: 100%;
          background: linear-gradient(90deg, #8a2be2, #e94560);
        }
        
        /* Recent Activity Styles */
        .activity-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .activity-item {
          display: flex;
          align-items: center;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #f0f0f0;
        }
        
        .activity-item:last-child {
          border-bottom: none;
        }
        
        .activity-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background-color: #f0f5ff;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1rem;
        }
        
        .activity-icon.document {
          background-color: #e3f2fd;
        }
        
        .activity-icon.message {
          background-color: #e8f5e9;
        }
        
        .activity-icon.security {
          background-color: #fff8e1;
        }
        
        .activity-icon.access {
          background-color: #f3e5f5;
        }
        
        .activity-details {
          flex: 1;
        }
        
        .activity-description {
          font-size: 0.9rem;
          color: #333;
          margin-bottom: 0.25rem;
        }
        
        .activity-time {
          font-size: 0.8rem;
          color: #999;
        }
        
        /* Quick Access Styles */
        .quick-links {
          padding: 1rem 1.5rem;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
          gap: 1rem;
        }
        
        .quick-link-button {
          background-color: #f8f9fa;
          border: none;
          border-radius: 8px;
          padding: 1rem 0.5rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .quick-link-button:hover {
          background-color: #f0f0f0;
        }
        
        .button-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background-color: #fff;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .quick-link-button span {
          font-size: 0.8rem;
          color: #555;
        }
        
        /* Profile Styles */
        .profile-header {
          margin-bottom: 2rem;
        }
        
        .profile-header h2 {
          margin: 0 0 0.5rem;
          color: #333;
        }
        
        .profile-subtitle {
          margin: 0;
          color: #777;
          font-size: 0.9rem;
        }
        
        .profile-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
        }
        
        .profile-card {
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          margin-bottom: 1.5rem;
        }
        
        .edit-button {
          background-color: transparent;
          color: #8a2be2;
          border: none;
          display: flex;
          align-items: center;
          font-size: 0.85rem;
          cursor: pointer;
        }
        
        .edit-button svg {
          margin-right: 0.5rem;
        }
        
        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          padding: 1.5rem;
        }
        
        .info-item {
          margin-bottom: 1rem;
        }
        
        .info-label {
          font-size: 0.8rem;
          color: #777;
          margin-bottom: 0.25rem;
        }
        
        .info-value {
          font-size: 0.95rem;
          color: #333;
        }
        
        .info-list {
          padding: 1.5rem;
        }
        
        .info-list .info-item {
          display: flex;
          align-items: center;
          margin-bottom: 1.5rem;
        }
        
        .info-list .info-item:last-child {
          margin-bottom: 0;
        }
        
        .info-icon {
          width: 36px;
          height: 36px;
          background-color: #f0f5ff;
          border-radius: 50%;
          margin-right: 1rem;
        }
        
        .info-details {
          flex: 1;
        }
        
        .projects-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .project-item {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #f0f0f0;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        
        .project-item:last-child {
          border-bottom: none;
        }
        
        .project-name {
          font-weight: 500;
          color: #333;
        }
        
        .project-role {
          font-size: 0.85rem;
          color: #777;
        }
        
        .project-status {
          font-size: 0.75rem;
          text-transform: uppercase;
          color: #8a2be2;
        }
        
        .empty-state {
          text-align: center;
          padding: 2rem;
          color: #999;
        }
        
        .empty-state p {
          margin: 0 0 0.5rem;
        }
        
        .empty-subtext {
          font-size: 0.85rem;
          color: #aaa;
        }
        
        /* Tasks Styles */
        .tasks-header {
          margin-bottom: 2rem;
        }
        
        .tasks-header h2 {
          margin: 0 0 0.5rem;
          color: #333;
        }
        
        .tasks-subtitle {
          margin: 0;
          color: #777;
          font-size: 0.9rem;
        }
        
        .add-task-container {
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          padding: 1.5rem;
          margin-bottom: 1.5rem;
        }
        
        .add-task-form {
          display: flex;
          align-items: center;
        }
        
        .task-input {
          flex: 1;
          padding: 0.75rem;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          font-size: 0.9rem;
          margin-right: 1rem;
        }
        
        .add-task-button {
          background-color: #8a2be2;
          color: white;
          border: none;
          border-radius: 4px;
          padding: 0.75rem 1.5rem;
          display: flex;
          align-items: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .add-task-button svg {
          margin-right: 0.5rem;
        }
        
        .add-task-button:hover {
          background-color: #7823c7;
        }
        
        .task-list-container {
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }
        
        .task-filters {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #f0f0f0;
          display: flex;
          align-items: center;
        }
        
        .filter-button {
          background-color: transparent;
          border: none;
          color: #777;
          padding: 0.5rem 1rem;
          margin-right: 0.5rem;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .filter-button.active {
          background-color: #f0f0f0;
          color: #333;
          font-weight: 500;
        }
        
        .task-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .task-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #f0f0f0;
        }
        
        .task-item:last-child {
          border-bottom: none;
        }
        
        .task-main {
          display: flex;
          align-items: center;
          flex: 1;
        }
        
        .task-checkbox {
          position: relative;
          display: inline-block;
          width: 20px;
          height: 20px;
          margin-right: 0.75rem;
        }
        
        .task-checkbox input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        
        .task-text {
          font-size: 0.95rem;
          color: #333;
        }
        
        .task-item.completed .task-text {
          text-decoration: line-through;
          color: #aaa;
        }
        
        .task-actions {
          display: flex;
          align-items: center;
        }
        
        .task-delete {
          background-color: transparent;
          border: none;
          color: #999;
          cursor: pointer;
          padding: 0.25rem;
          margin-left: 0.5rem;
          transition: all 0.2s ease;
        }
        
        .task-delete:hover {
          color: #e94560;
        }
        
        /* Placeholder Styles */
        .placeholder-content {
          text-align: center;
          padding: 4rem;
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .placeholder-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background-color: #f0f0f0;
          margin: 0 auto 1.5rem;
        }
        
        .placeholder-content h2 {
          margin: 0 0 1rem;
          color: #333;
        }
        
        .placeholder-content p {
          margin: 0;
          color: #777;
        }
        
        /* Responsive Styles */
        @media (max-width: 768px) {
          .portal-content {
            flex-direction: column;
          }
          
          .portal-sidebar {
            width: 100%;
            height: auto;
            position: static;
          }
          
          .dashboard-grid {
            grid-template-columns: 1fr;
          }
          
          .profile-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default NecrovaEmployeePortal;
     